/* Polyfill: ensure e.target.closest never throws */
if (!Element.prototype._origClosest) { Element.prototype._origClosest = Element.prototype.closest; }
if (typeof Node !== "undefined" && !Node.prototype.closest) { Node.prototype.closest = function(s) { return this.parentElement ? this.parentElement.closest(s) : null; }; }

/**
 * CO-VRD Theme — Global JavaScript (Consolidated)
 * ================================================
 * Single entry-point for all shared theme interactions.
 * Architecture:
 *   §1  Pub/Sub Event Bus
 *   §2  Focus Trap / Release
 *   §3  Debounce / Throttle
 *   §4  Currency Formatter
 *   §5  Drawer Manager (generic open/close/toggle)
 *   §6  Cart Count Sync
 *   §7  Scroll Direction (sticky header)
 *   §8  Accordion
 *   §9  Lazy Section Observer
 *   §10 Lazy Image Blur-Up Observer
 *   §11 Product Card Prefetch
 *   §12 Async CSS Loader Fallback
 *   §13 Smooth Scroll Anchors
 *
 * Memory: every listener is delegated or uses a single
 * IntersectionObserver instance. No per-element bindings.
 * ================================================
 */
(function () {
  'use strict';

  /* ============================================================
     §1  Pub/Sub Event Bus
     Lightweight publish / subscribe for decoupled modules.
     Usage:
       theme.pubsub.on('cart:updated', fn);
       theme.pubsub.emit('cart:updated', { item_count: 3 });
       theme.pubsub.off('cart:updated', fn);
     ============================================================ */
  var subscribers = {};

  var pubsub = {
    on: function (event, fn) {
      if (!subscribers[event]) subscribers[event] = [];
      subscribers[event].push(fn);
      return function () { pubsub.off(event, fn); }; // returns unsubscribe fn
    },

    off: function (event, fn) {
      if (!subscribers[event]) return;
      subscribers[event] = subscribers[event].filter(function (f) { return f !== fn; });
    },

    emit: function (event, data) {
      if (subscribers[event]) {
        subscribers[event].forEach(function (fn) { fn(data); });
      }
      /* Also dispatch a native CustomEvent so legacy listeners still work */
      document.dispatchEvent(new CustomEvent(event, { detail: data }));
    }
  };

  /* Expose on a namespaced global */
  window.theme = window.theme || {};
  window.theme.pubsub = pubsub;


  /* ============================================================
     §2  Focus Trap / Release
     ============================================================ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  window.trapFocus = function (container, firstEl) {
    var focusable = Array.from(container.querySelectorAll(FOCUSABLE));
    if (!focusable.length) return;
    var first = firstEl || focusable[0];
    var last  = focusable[focusable.length - 1];

    first.focus();

    function handler(e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
      }
    }
    container.addEventListener('keydown', handler);
    container._trapCleanup = function () { container.removeEventListener('keydown', handler); };
  };

  window.releaseFocus = function (container) {
    if (container._trapCleanup) { container._trapCleanup(); delete container._trapCleanup; }
  };


  /* ============================================================
     §3  Debounce / Throttle
     ============================================================ */
  window.debounce = function (fn, wait) {
    var t;
    return function () {
      var ctx = this, args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  };

  window.throttle = function (fn, limit) {
    var waiting = false;
    return function () {
      if (waiting) return;
      fn.apply(this, arguments);
      waiting = true;
      setTimeout(function () { waiting = false; }, limit);
    };
  };


  /* ============================================================
     §4  Currency Formatter (Shopify money formats)
     ============================================================ */
  window.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || (window.Shopify && window.Shopify.locale && window.Shopify.locale.moneyFormat) || '${{amount}}';

    function addCommas(n) {
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    var value = '';
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = addCommas((cents / 100).toFixed(2));
        break;
      case 'amount_no_decimals':
        value = addCommas(Math.round(cents / 100));
        break;
      case 'amount_with_comma_separator':
        value = (cents / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = Math.round(cents / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        break;
    }
    return formatString.replace(placeholderRegex, value);
  };


  /* ============================================================
     §5  Drawer Manager
     Generic open/close for any drawer (cart, mobile nav, search).
     Works with data attributes:
       data-drawer-open="[drawer-id]"
       data-drawer-close="[drawer-id]"
       data-drawer="[drawer-id]"
       data-drawer-overlay="[drawer-id]"
     ============================================================ */
  var openDrawers = [];

  function openDrawer(id) {
    var drawer = document.querySelector('[data-drawer="' + id + '"]');
    if (!drawer || openDrawers.indexOf(id) !== -1) return;

    drawer.hidden = false;
    drawer.offsetHeight; // force reflow for transition
    drawer.classList.add('drawer--open');
    document.body.style.overflow = 'hidden';
    openDrawers.push(id);

    window.trapFocus(drawer);
    pubsub.emit('drawer:opened', { id: id });
  }

  function closeDrawer(id) {
    var drawer = document.querySelector('[data-drawer="' + id + '"]');
    if (!drawer) return;

    drawer.classList.remove('drawer--open');
    document.body.style.overflow = '';
    window.releaseFocus(drawer);

    drawer.addEventListener('transitionend', function handler() {
      if (!drawer.classList.contains('drawer--open')) {
        drawer.hidden = true;
      }
      drawer.removeEventListener('transitionend', handler);
    });

    openDrawers = openDrawers.filter(function (d) { return d !== id; });
    pubsub.emit('drawer:closed', { id: id });
  }

  /* Delegated click handlers */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(opener.dataset.drawerOpen);
      return;
    }
    var closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      closeDrawer(closer.dataset.drawerClose);
      return;
    }
    var overlay = e.target.closest('[data-drawer-overlay]');
    if (overlay) {
      closeDrawer(overlay.dataset.drawerOverlay);
    }
  });

  /* Expose for programmatic use */
  window.theme.drawer = { open: openDrawer, close: closeDrawer };


  /* ============================================================
     §6  Cart Count Badge Sync
     ============================================================ */
  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  /* Listen via Pub/Sub */
  pubsub.on('cart:updated', function (data) {
    if (data && typeof data.item_count !== 'undefined') updateCartCount(data.item_count);
  });

  pubsub.on('cart:added', function (data) {
    if (data && typeof data.item_count !== 'undefined') updateCartCount(data.item_count);
  });

  /* Backward-compat: also listen for native CustomEvents from inline scripts */
  document.addEventListener('cart:updated', function (e) {
    if (e.detail && typeof e.detail.item_count !== 'undefined') updateCartCount(e.detail.item_count);
  });
  document.addEventListener('cart:added', function (e) {
    if (e.detail && typeof e.detail.item_count !== 'undefined') updateCartCount(e.detail.item_count);
  });


  /* ============================================================
     §7  Scroll Direction Detection (Sticky Header)
     ─── Now handled by assets/header.js §1 ───
     Kept as a no-op for backward compatibility.
     ============================================================ */


  /* ============================================================
     §8  Accordion (Delegated)
     Works on any <details> with [data-accordion-group].
     Exclusive open: opening one closes siblings.
     ============================================================ */
  document.addEventListener('toggle', function (e) {
    var details = e.target;
    if (!details.matches || !details.matches('details[data-accordion-group]')) return;
    if (!details.open) return;

    var group = details.getAttribute('data-accordion-group');
    document.querySelectorAll('details[data-accordion-group="' + group + '"]').forEach(function (d) {
      if (d !== details && d.open) d.removeAttribute('open');
    });
  }, true);


  /* ============================================================
     §9  Lazy Section Loading Observer
     Adds .section--loaded when section scrolls into view.
     ============================================================ */
  if ('IntersectionObserver' in window) {
    var sectionObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('section--loaded');
          sectionObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px' });

    document.querySelectorAll('.section--lazy').forEach(function (el) {
      sectionObserver.observe(el);
    });
  }


  /* ============================================================
     §10  Lazy Image Blur-Up Observer
     Watches .lazy-image containers. When visible, loads the
     real <img> src and fades from blurred LQIP to sharp.
     ============================================================ */
  if ('IntersectionObserver' in window) {
    var imageObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var container = entry.target;
        var img = container.querySelector('.lazy-image__src');
        if (!img) { imageObserver.unobserve(container); return; }

        /* If image has data-srcset, swap it in */
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
          img.removeAttribute('data-srcset');
        }
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        }

        /* On load, reveal */
        function reveal() {
          container.classList.add('lazy-image--loaded');
          imageObserver.unobserve(container);
        }

        if (img.complete && img.naturalWidth > 0) {
          reveal();
        } else {
          img.addEventListener('load', reveal, { once: true });
          img.addEventListener('error', reveal, { once: true });
        }
      });
    }, { rootMargin: '200px 0px' });

    document.querySelectorAll('.lazy-image[data-lazy-image]').forEach(function (el) {
      imageObserver.observe(el);
    });

    /* Re-observe dynamically added images (e.g. AJAX pagination) */
    pubsub.on('images:observe', function () {
      document.querySelectorAll('.lazy-image[data-lazy-image]:not(.lazy-image--loaded)').forEach(function (el) {
        imageObserver.observe(el);
      });
    });
  }


  /* ============================================================
     §11  Product Card Prefetch on Hover
     Injects <link rel="prefetch"> on mouseenter, deduplicates.
     Uses requestIdleCallback for non-blocking insertion.
     ============================================================ */
  var prefetchedUrls = {};

  function prefetchUrl(url) {
    if (prefetchedUrls[url]) return;
    prefetchedUrls[url] = true;

    var schedule = window.requestIdleCallback || function (cb) { setTimeout(cb, 1); };
    schedule(function () {
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    });
  }

  /* Delegated — listens on product card links */
  document.addEventListener('mouseenter', function (e) {
    var card = e.target.closest('.product-card');
    if (!card) return;
    var anchor = card.querySelector('.product-card__link');
    if (anchor && anchor.href) {
      prefetchUrl(anchor.href);
    }
  }, true); // useCapture for mouseenter delegation

  /* Also handle touch devices — prefetch on touchstart */
  document.addEventListener('touchstart', function (e) {
    var card = e.target.closest('.product-card');
    if (!card) return;
    var anchor = card.querySelector('.product-card__link');
    if (anchor && anchor.href) {
      prefetchUrl(anchor.href);
    }
  }, { passive: true });


  /* ============================================================
     §12  Async CSS Loader Fallback
     For browsers that don't support link[rel=preload] natively.
     Checks if preloaded stylesheets switched to rel=stylesheet.
     ============================================================ */
  document.querySelectorAll('link[data-async-css]').forEach(function (link) {
    if (link.rel === 'preload') {
      link.onload = function () {
        this.onload = null;
        this.rel = 'stylesheet';
      };
      /* Fallback: if onload doesn't fire within 3s, force-load */
      setTimeout(function () {
        if (link.rel === 'preload') {
          link.rel = 'stylesheet';
        }
      }, 3000);
    }
  });


  /* ============================================================
     §13  Smooth Scroll for Anchor Links (Delegated)
     ============================================================ */
  document.addEventListener('click', function (e) {
    var link = e.target.closest('a[href^="#"]');
    if (!link) return;
    var hash = link.getAttribute('href');
    if (hash.length <= 1) return; // skip bare '#'
    var target = document.querySelector(hash);
    if (target) {
      e.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });

})();
